import { IRequestContext } from '@msdyn365-commerce/core-internal';
import { ITelemetry } from '@msdyn365-commerce/telemetry-internal';
import { GetProductFacet, ProductSearchBuilder, Searcher } from '@relewise/client';
import { ALL_VARIANTS_EXPLODE_COUNT, RELEWISE_DISPLAYED_AT_LOCATION } from '../const';
import { InitializedRelewiseService, BaseRelewiseService } from './BaseRelewiseService/BaseRelewiseService';
import { RelewiseMappingService } from './MappingService';

interface InitializedService extends InitializedRelewiseService {
    relewiseMappingService: RelewiseMappingService;
    searcher: Searcher;
    categoryName: string;
}

export class RelewiseFilterService extends BaseRelewiseService<InitializedService> {
    protected searcher: Searcher | undefined;
    protected relewiseMappingService: RelewiseMappingService | undefined;

    protected categoryName: string | undefined;
    protected urlCategoryKey: string | undefined;

    constructor(request: IRequestContext, telemetry: ITelemetry) {
        super(request, telemetry, 'Filter Service');

        if (!this.isEnabled()) {
            return;
        }

        this.relewiseMappingService = new RelewiseMappingService(request, telemetry);
        this.searcher = new Searcher(this.relewiseDatasetId, this.relewiseApiKey, {
            serverUrl: this.relewiseUrl
        });

        this.urlCategoryKey = this.safeDecodeURIComponent(request.urlTokens?.categories?.[0] ?? '');
    }

    public async getFilters(categoryRecordId?: string, searchTerm?: string) {
        if (!this.isEnabled()) {
            return;
        }

        try {
            const displayedAtLocation = categoryRecordId
                ? RELEWISE_DISPLAYED_AT_LOCATION.CATEGORY_PLP
                : RELEWISE_DISPLAYED_AT_LOCATION.SEARCH_PLP;
            const settings = await this.getApiRequestSettings(displayedAtLocation);

            if (categoryRecordId) {
                const categoryId = this.urlCategoryKey ? this.relewiseMappingService.prefixId(this.urlCategoryKey) : undefined;
                if (!categoryId) {
                    throw new Error(
                        `Missing category key from urlTokens for recordId=${categoryRecordId} environment=${this.environmentName}`
                    );
                }
                this.categoryName = categoryId;
            }
            const builder = new ProductSearchBuilder(settings)
                .pagination(p => p.setPageSize(0))
                .setVariantRequestSettings(variantSettings => {
                    variantSettings.setMaxVariantsPerProduct(ALL_VARIANTS_EXPLODE_COUNT);
                })
                .searchConstraints(searchConstraints => {
                    searchConstraints.setResultMustHaveVariantConstraint({
                        exceptWhenProductHasNoVariants: false
                    });
                })
                .facets(f => {
                    f.addSalesPriceRangeFacet('Product', undefined, undefined);
                    f.addCategoryFacet('ImmediateParent', undefined);
                    f.addProductDataStringValueFacet('Size', 'Variant', undefined);
                    f.addProductDataStringValueFacet('ColorRefinerGroup', 'Variant', undefined);
                })
                .filters(f => {
                    if (this.categoryName) {
                        f.addProductCategoryIdFilter('Ancestor', this.categoryName);
                    }
                    this.addProductEnvironmentFilter(f);
                    this.addProductAndVariantStockFilters(f);
                });
            if (searchTerm) {
                builder.setTerm(searchTerm);
            }
            const response = await this.searcher.searchProducts(builder.build());

            if (!response?.results) {
                return {
                    priceRange: { minPrice: 0, maxPrice: 0 },
                    categories: [],
                    sizes: [],
                    colorGroups: []
                };
            }

            const priceRangeFacet = response.facets ? GetProductFacet.salesPriceRange(response.facets, 'Product') : null;
            const minPrice = priceRangeFacet?.available?.value?.lowerBoundInclusive ?? 0;
            const maxPrice = priceRangeFacet?.available?.value?.upperBoundInclusive ?? 0;

            const categoryFacet = response.facets ? GetProductFacet.category(response.facets, 'ImmediateParent') : null;
            const categories =
                categoryFacet?.available
                    ?.filter(c => c.value?.id && c.value?.displayName)
                    .map(c => ({ id: c.value!.id!, displayName: c.value!.displayName! })) ?? [];

            const sizeFacet = response.facets ? GetProductFacet.dataString(response.facets, 'Size', 'Variant') : null;
            const sizes = sizeFacet?.available?.filter(s => s.value).map(s => ({ value: s.value!, hits: s.hits })) ?? [];

            const colorFacet = response.facets ? GetProductFacet.dataString(response.facets, 'ColorRefinerGroup', 'Variant') : null;
            const colorGroups = colorFacet?.available?.filter(s => s.value).map(s => ({ value: s.value!, hits: s.hits })) ?? [];

            return {
                priceRange: { minPrice, maxPrice },
                categories,
                sizes,
                colorGroups
            };
        } catch (err) {
            this.logError(`getFilters request failed: ${err instanceof Error ? err.message : String(err)}`);
            return;
        }
    }
}
